import {
  Dialog,
  DialogContentText,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Select,
  MenuItem,
  SelectChangeEvent,
  FormControl,
  InputLabel,
} from '@mui/material';
import InfoPopup from 'components/InfoPopup';

import { parseISO } from 'date-fns';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { getTracesAroundLogItem } from '../../api/snapshotAlarmLogApi';
import { TraceHistory } from '../../types';
import {
  findClosestTraceIndex,
  removeDuplicateSnapshots,
} from '../alarmLog/utils/logManipulation';
import { SocketAlarm } from '../spectrasWebSocketServer/types';
import { useUserDateTime } from '../timezone/hooks/useUserDateTime';
import { useAllSnapshotsWithoutCrosspol } from '../snapshots/api/getAllSnapshotsWithoutCrosspol';
import { formatInTimeZone } from 'date-fns-tz';

interface Props {
  open: boolean;
  handleClose: () => void;
  alarm: SocketAlarm | null;
  handleNotificationDrawerClose: () => void;
}

const AlarmDialog = ({
  open,
  handleClose,
  alarm,
  handleNotificationDrawerClose,
}: Props) => {
  const { data: snapshots } = useAllSnapshotsWithoutCrosspol({});
  const [noHistoryPopupOpen, setNoHistoryPopupOpen] = useState(false);
  const [selectedLogItem, setSelectedLogItem] = useState('');
  const navigate = useNavigate();
  const { dateFormatWithZeros, timeFormat, timezone } = useUserDateTime();

  if (!alarm) return <></>;

  if (alarm.start_mode && alarm.stopped) return <></>;

  const formattedDateTime = formatInTimeZone(
    parseISO(alarm?.timestamp),
    timezone,
    `${dateFormatWithZeros} ${timeFormat}`
  );

  const handleSnapshotSelect = (event: SelectChangeEvent) => {
    setSelectedLogItem(event.target.value);
  };

  const filteredAlarmLogItems = removeDuplicateSnapshots(alarm.alarm_log_items);

  const handleGoToTraceHistory = async () => {
    let traces: TraceHistory[];
    if (filteredAlarmLogItems.length === 1) {
      traces = await getTracesAroundLogItem(
        alarm.alarm_log_items[0].alarm_log_item_id,
        50,
        50
      );
    } else {
      traces = await getTracesAroundLogItem(Number(selectedLogItem), 50, 50);
    }
    if (traces.length === 0) {
      setNoHistoryPopupOpen(true);
    } else {
      const fromDate = traces[0].time;
      const toDate = traces[traces.length - 1].time;
      if (fromDate > alarm.timestamp || toDate < alarm.timestamp) {
        setNoHistoryPopupOpen(true);
        return;
      }
      const centerIndex = findClosestTraceIndex(traces, alarm.timestamp);
      navigate(
        `/trace-history?analyzer=${traces[0].analyzer_id}&snapshot=${traces[0].snapshot_id}&start=${fromDate}&end=${toDate}&trace=${centerIndex}`
      );
      handleClose();
      handleNotificationDrawerClose();
    }
  };

  return (
    <>
      <Dialog
        transitionDuration={0}
        fullWidth
        maxWidth={'sm'}
        open={open}
        onClose={handleClose}
      >
        <DialogTitle>Alarm: {alarm?.name}</DialogTitle>
        <DialogContent style={{ paddingBottom: '.7rem' }}>
          <DialogContentText>{formattedDateTime}</DialogContentText>
          <TextField
            variant='outlined'
            fullWidth
            multiline
            value={alarm?.message}
          ></TextField>
        </DialogContent>
        <DialogActions style={{ paddingTop: 0 }}>
          {filteredAlarmLogItems.length > 1 && (
            <FormControl sx={{ minWidth: 250, paddingRight: '.3rem' }}>
              <InputLabel id='select-snapshot-label'>Snapshot</InputLabel>
              <Select
                labelId='select-snapshot-label'
                value={selectedLogItem}
                onChange={handleSnapshotSelect}
                label='Snapshot'
              >
                {filteredAlarmLogItems.map((item) => (
                  <MenuItem
                    key={item.alarm_log_item_id}
                    value={item.alarm_log_item_id}
                  >
                    {
                      snapshots?.find(
                        (snapshot) => snapshot.id === item.snapshot_id
                      )?.name
                    }
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <Button
            color='primary'
            onClick={() => {
              handleGoToTraceHistory().then();
            }}
            disabled={filteredAlarmLogItems.length > 1 && !selectedLogItem}
          >
            Go To Trace History
          </Button>
          <Button color='primary' onClick={handleClose}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
      <InfoPopup
        openCondition={noHistoryPopupOpen}
        title='No History Found'
        message='There is no recorded trace history for this alarm and the selected snapshot.'
        error
      />
    </>
  );
};

export default AlarmDialog;
